import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/audit/notices/index.tsx
 * @description B 稽核通知列表頁（M2）。
 *
 * 功能：
 * - 列出所有稽核通知（audit_notices）
 * - 顯示：通知類型 / 排定日期 / 發送時間 / 案件
 * - Manager/Admin 可建立通知
 *
 * 角色：
 * - admin/manager：可建立通知
 * - auditor：只讀
 *
 * 視覺：ListPageShell + StatusPill
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Drawer, Form, Input, Select, Table } from 'antd';
import { message } from '@/lib/antd-app';
import { Bell, Plus } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { ListPageShell, StatusPill, type StatusPillProps } from '@/components/shell';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/audit/notices/')({ component: AuditNoticesPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface AuditNotice {
  id: number;
  caseId: number;
  caseCode: string | null;
  noticeType: string;
  scheduledDate: string | null;
  sentAt: string | null;
  body: string | null;
  createdAt: string;
}

interface CaseOption {
  id: number;
  caseCode: string;
}

interface ListResponse {
  items: AuditNotice[];
  total: number;
}

// ─── 通知類型 ─────────────────────────────────────────────────────────────────

const NOTICE_TYPE_OPTIONS = [
  { value: 'scheduled', label: '排定稽核通知' },
  { value: 'unannounced', label: '不預告稽核通知' },
  { value: 'follow_up', label: '追蹤稽核通知' },
  { value: 'suspension', label: '稽核暫停通知' },
  { value: 'cancellation', label: '稽核取消通知' },
];

const NOTICE_TYPE_TONE: Record<string, StatusPillProps['tone']> = {
  scheduled: 'brand',
  unannounced: 'warning',
  follow_up: 'info',
  suspension: 'danger',
  cancellation: 'muted',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * B 稽核通知列表頁面。
 */
function AuditNoticesPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user!);
  const isManager = user.roles.includes('admin') || user.roles.includes('manager');

  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form] = Form.useForm();

  /** 查詢稽核通知列表 */
  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['audit-notices', page],
    queryFn: () => apiClient.get(`/api/audit-notices?page=${page}&pageSize=${pageSize}`),
  });

  /** 查詢案件列表（用於 Select） */
  const { data: casesData } = useQuery<{ items: CaseOption[] }>({
    queryKey: ['cases-options'],
    queryFn: () => apiClient.get('/api/cases?pageSize=100'),
    enabled: drawerOpen,
  });

  const listData = data;
  const cases = casesData?.items ?? [];

  /** 建立通知 */
  const createMutation = useMutation({
    mutationFn: ({ caseId, body }: { caseId: number; body: unknown }) =>
      apiClient.post(`/api/cases/${caseId}/notices`, body),
    onSuccess: () => {
      message.success('稽核通知已建立');
      setDrawerOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['audit-notices'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const columns: ColumnsType<AuditNotice> = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      width: 160,
      render: (v) => (v ? <span style={{ fontFamily: 'monospace' }}>{v}</span> : '—'),
    },
    {
      title: '通知類型',
      dataIndex: 'noticeType',
      width: 160,
      render: (v: string) => (
        <StatusPill tone={NOTICE_TYPE_TONE[v] ?? 'neutral'} size="sm">
          {NOTICE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v}
        </StatusPill>
      ),
    },
    {
      title: '排定稽核日期',
      dataIndex: 'scheduledDate',
      width: 140,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '發送時間',
      dataIndex: 'sentAt',
      width: 130,
      render: (d: string | null) =>
        d ? (
          <StatusPill tone="success" size="sm">
            {formatDate(d)}
          </StatusPill>
        ) : (
          <StatusPill tone="muted" size="sm">
            未發送
          </StatusPill>
        ),
    },
    { title: '通知內容', dataIndex: 'body', ellipsis: true, render: (v) => v ?? '—' },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 120,
      render: (d: string) => formatDate(d),
    },
  ];

  return (
    <ListPageShell
      title="稽核通知"
      description="現場稽核排定、不預告、追蹤及暫停通知管理"
      meta={
        <StatusPill tone="info" icon={<Bell size={12} />} size="sm">
          通知管理
        </StatusPill>
      }
      actions={
        isManager ? (
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setDrawerOpen(true)}>
            建立通知
          </Button>
        ) : undefined
      }
    >
      <Table<AuditNotice>
        dataSource={listData?.items ?? []}
        rowKey="id"
        loading={isLoading}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total: listData?.total ?? 0,
          onChange: setPage,
          showTotal: (t) => `共 ${t} 筆`,
        }}
      />

      {/* 建立通知 Drawer */}
      <Drawer
        title="建立稽核通知"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button
              type="primary"
              loading={createMutation.isPending}
              onClick={() => {
                void form.validateFields().then((v: Record<string, unknown>) => {
                  const caseId = v.caseId;
                  const rest = Object.fromEntries(
                    Object.entries(v).filter(([k]) => k !== 'caseId'),
                  );
                  createMutation.mutate({ caseId: Number(caseId), body: rest });
                });
              }}
            >
              提交
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="caseId"
            label="選擇案件"
            rules={[{ required: true, message: '請選擇案件' }]}
          >
            <Select
              showSearch
              placeholder="搜尋案件編號"
              options={cases.map((c) => ({ value: c.id, label: c.caseCode }))}
              filterOption={(input, opt) =>
                String(opt?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="noticeType"
            label="通知類型"
            rules={[{ required: true, message: '請選擇通知類型' }]}
          >
            <Select options={NOTICE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="scheduledDate" label="排定稽核日期">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="body" label="通知內容">
            <Input.TextArea rows={5} placeholder="稽核通知正文..." />
          </Form.Item>
        </Form>
      </Drawer>
    </ListPageShell>
  );
}
